import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCurrencyRates } from './useCurrencyRates'; // För att hämta rates
import type { CurrencyRates } from './useCurrencyRates';

// Typ för valutakoder
export type CurrencyCode = 'SEK' | 'EUR' | 'USD' | 'ILS';

// Minimal mapping land -> valuta
const countryToCurrency: Record<string, CurrencyCode> = {
  NL: 'EUR',
  IL: 'ILS',
  US: 'USD',
  SE: 'SEK',
};

// Minimal geo-IP-funktion
async function fetchCountry(): Promise<string> {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    return data.country || 'SE';
  } catch {
    return 'SE';
  }
}

// Context-typ
interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (c: CurrencyCode) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Context och provider för valutakurser från databasen
const CurrencyRatesContext = createContext<{
  rates: CurrencyRates | null;
  isLoading: boolean;
  error: any;
}>({ rates: null, isLoading: false, error: null });

export const CurrencyRatesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: rates, isLoading, error } = useCurrencyRates();
  return (
    <CurrencyRatesContext.Provider value={{ rates, isLoading, error }}>
      {children}
    </CurrencyRatesContext.Provider>
  );
};

export function useCurrencyRatesContext() {
  return useContext(CurrencyRatesContext);
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrency] = useState<CurrencyCode>('SEK');

  useEffect(() => {
    fetchCountry().then(country => {
      setCurrency(countryToCurrency[country] || 'SEK');
    });
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {/* Lägg in valutakurs-provider runt hela appen */}
      <CurrencyRatesProvider>{children}</CurrencyRatesProvider>
    </CurrencyContext.Provider>
  );
};

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency måste användas inom CurrencyProvider');
  return ctx;
}
